import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, PenLine, X as XIcon } from "lucide-react";
import { isoWeekKey } from "./helpers";

const LS_KEY_PREFIX = "goal-weekly-reflect-";

export function isWeeklyReflectionDue(goal) {
    if (!goal || goal.status !== "active") return false;
    // Only surface once the goal has been alive for at least 3 days —
    // avoids nagging on brand-new goals.
    try {
        if (goal.created_at) {
            const created = new Date(goal.created_at).getTime();
            if (Date.now() - created < 3 * 86400000) return false;
        }
    } catch (_e) { /* ignore */ }
    try {
        const marked = localStorage.getItem(LS_KEY_PREFIX + goal.id);
        return marked !== isoWeekKey();
    } catch (_e) {
        return true;
    }
}

/**
 * Weekly Reflection Card — a soft, once-a-week nudge on the goal view.
 *
 * Design rules (strict, gracious):
 * - No streaks, no percentages, no pace framing.
 * - Two options only: "Write a line" (opens Journal, prefilled) or "Not now" (defers this week).
 * - Both actions mark the current ISO week as handled so the card disappears until next week.
 */
export default function WeeklyReflectCard({ goal }) {
    const nav = useNavigate();
    const [hidden, setHidden] = useState(false);

    const weekLabel = useMemo(() => {
        // "the week of Feb 10" — the Monday of the current ISO week
        const now = new Date();
        const day = now.getDay() || 7; // 1..7 (Mon..Sun)
        const monday = new Date(now);
        monday.setDate(now.getDate() - (day - 1));
        return monday.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    }, []);

    if (hidden || !isWeeklyReflectionDue(goal)) return null;

    const markHandled = () => {
        try { localStorage.setItem(LS_KEY_PREFIX + goal.id, isoWeekKey()); } catch (_e) { /* ignore */ }
    };

    const writeReflection = () => {
        markHandled();
        try {
            const meta = goal.category + (goal.theme ? ` · ${goal.theme}` : "");
            sessionStorage.setItem("goal-weekly-reflect-prompt", JSON.stringify({
                goal_id: goal.id,
                body: `${goal.title} — the week of ${weekLabel}.\n\n${meta}\n\nHow did it go?\n\n`,
            }));
        } catch (_e) { /* ignore */ }
        nav("/journal/new?fromGoalReflect=1");
    };

    const notNow = () => {
        markHandled();
        setHidden(true);
    };

    return (
        <aside
            data-testid="weekly-reflect-card"
            className="mt-4 p-5 rounded-3xl bg-sage-paper border border-moss/20"
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="font-ui text-[10px] uppercase tracking-[0.2em] text-moss-deep font-semibold flex items-center gap-1.5">
                        <Sparkles size={11} /> A week with God
                    </div>
                    <p className="mt-1 scripture text-[18px] text-ink leading-snug">How did this week go?</p>
                    <p className="mt-1 font-ui text-[12px] italic text-ink-70">
                        Just a line, if you'd like — nothing more is asked.
                    </p>
                </div>
                <button
                    data-testid="weekly-reflect-dismiss"
                    onClick={notNow}
                    className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-ink-40 hover:text-ink hover:bg-ink-10 transition"
                    aria-label="Not now"
                    title="Not now"
                >
                    <XIcon size={14} />
                </button>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
                <button
                    data-testid="weekly-reflect-write"
                    onClick={writeReflection}
                    className="px-4 py-2 rounded-full bg-moss text-white font-ui text-[13px] font-medium hover:bg-moss-deep transition inline-flex items-center gap-1.5"
                >
                    <PenLine size={14} /> Write a line
                </button>
                <button
                    data-testid="weekly-reflect-later"
                    onClick={notNow}
                    className="px-4 py-2 rounded-full bg-white border border-ink-10 font-ui text-[13px] text-ink-70 hover:border-moss/40 transition"
                >
                    Not now
                </button>
            </div>
        </aside>
    );
}
